import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Plus, Video, X } from 'lucide-react';
import { useMovePost, useSocialPosts } from '../../hooks/useSocialPosts';
import { useClients } from '../../hooks/useClients';
import { usePeople } from '../../hooks/usePeople';
import { usePermissions } from '../../hooks/usePermissions';
import { useSidebar } from '../layout/SidebarContext';
import { useI18n } from '../../contexts/I18nContext';
import { fromDateKey, monthGrid, toDateKey, weekdayLabels } from '../../lib/calendarDates';
import { clientColor, clientLabel } from '../../lib/clientLabel';
import {
  APPROVAL_LABELS,
  APPROVAL_STATUSES,
  APPROVAL_TONES,
  FORMAT_LABELS,
  PLATFORM_COLORS,
  PLATFORM_LABELS,
  SOCIAL_PLATFORMS,
  type SocialPost,
} from '../../lib/socialPostsApi';
import { MultiSelect } from '../ui/MultiSelect';
import { Select } from '../ui/Select';
import { SkeletonTable } from '../ui/Skeleton';
import { CalendarDnd, DraggableCalendarItem, DroppableCalendarDay } from '../calendar/calendarDnd';
import { ToastProvider, useToast } from '../tasks/linear/Toast';
import { PostFormModal } from './PostFormModal';

export interface PostsCalendarFilters {
  clients: string[];
  platforms: string[];
  approval: string[];
  assignee: string;
}

export const EMPTY_POST_FILTERS: PostsCalendarFilters = {
  clients: [],
  platforms: [],
  approval: [],
  assignee: '',
};

interface PostsCalendarProps {
  year: number;
  /** 0-based. */
  month: number;
  filters: PostsCalendarFilters;
  onFiltersChange: (next: PostsCalendarFilters) => void;
  /** The client page's tab: one client only, its filter hidden. */
  lockedClientId?: string;
}

const MAX_VISIBLE = 4;

function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Calendar de postări: every client's social-media posts on the day they go
 * live. Each card says which client (its colour and nickname), which platform,
 * when, and how far the client's approval got; a click shows the caption and
 * the video link without leaving the calendar.
 */
export function PostsCalendar(props: PostsCalendarProps) {
  // Its own toast host: this also mounts on the client page, which has none.
  return (
    <ToastProvider>
      <PostsCalendarBody {...props} />
    </ToastProvider>
  );
}

/** The new publishing moment: the chosen day, at the post's own time of day. */
function publishAtOnDay(publishAt: string, dayKey: string): string {
  const time = new Date(publishAt);
  const next = fromDateKey(dayKey);
  next.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
  return next.toISOString();
}

function PostsCalendarBody({
  year,
  month,
  filters,
  onFiltersChange,
  lockedClientId,
}: PostsCalendarProps) {
  const { locale } = useI18n();
  const toast = useToast();
  const movePost = useMovePost();
  const { isMobile } = useSidebar();
  const { can } = usePermissions();
  const canCreate = can('socialPosts', 'canCreate');
  const canEdit = can('socialPosts', 'canEdit');
  const [creatingOn, setCreatingOn] = useState<string | null>(null);
  const [preview, setPreview] = useState<SocialPost | null>(null);

  const weeks = useMemo(() => monthGrid(year, month), [year, month]);
  const from = weeks[0][0].date;
  const to = new Date(weeks[5][6].date.getTime() + 86_400_000);
  const clientIds = lockedClientId ? [lockedClientId] : filters.clients;

  const { data, isLoading, isError } = useSocialPosts({
    from: from.toISOString(),
    to: to.toISOString(),
    clientId: clientIds,
    platform: filters.platforms,
    approval: filters.approval,
    assigneeId: filters.assignee || undefined,
  });

  const { data: clientsData } = useClients({ limit: 100, active: 'true', sort: 'name' });
  const { data: people } = usePeople({ active: 'true' });
  const clientOptions = useMemo(
    () => (clientsData?.data ?? []).map((c) => ({ value: c.id, label: clientLabel(c) })),
    [clientsData],
  );
  const assigneeOptions = useMemo(
    () =>
      (people ?? [])
        .filter((p) => p.userId)
        .map((p) => ({ value: p.userId as string, label: p.fullName })),
    [people],
  );

  const posts = data?.posts ?? [];
  const byDay = useMemo(() => {
    const map = new Map<string, SocialPost[]>();
    for (const p of posts) {
      const key = toDateKey(new Date(p.publishAt));
      const list = map.get(key) ?? [];
      list.push(p);
      map.set(key, list);
    }
    return map;
  }, [posts]);

  // Legend: the clients that actually appear this month.
  const legend = useMemo(() => {
    const seen = new Map<string, NonNullable<SocialPost['client']>>();
    for (const p of posts) if (p.client) seen.set(p.client.id, p.client);
    return [...seen.values()];
  }, [posts]);

  const set = (patch: Partial<PostsCalendarFilters>) => onFiltersChange({ ...filters, ...patch });
  const todayKey = toDateKey(new Date());

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        {!lockedClientId && (
          <MultiSelect
            label="Clienți"
            options={clientOptions}
            value={filters.clients}
            onChange={(clients) => set({ clients })}
            allLabel="Toți clienții"
            summaryLabel={(n) => `${n} clienți`}
            className="w-full sm:w-52"
          />
        )}
        <MultiSelect
          label="Platformă"
          options={SOCIAL_PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
          value={filters.platforms}
          onChange={(platforms) => set({ platforms })}
          allLabel="Toate platformele"
          summaryLabel={(n) => `${n} platforme`}
          className="w-full sm:w-48"
        />
        <MultiSelect
          label="Aprobare"
          options={APPROVAL_STATUSES.map((s) => ({ value: s, label: APPROVAL_LABELS[s] }))}
          value={filters.approval}
          onChange={(approval) => set({ approval })}
          allLabel="Orice status"
          summaryLabel={(n) => `${n} statusuri`}
          className="w-full sm:w-48"
        />
        <Select
          label="Responsabil"
          clearable
          searchable
          value={filters.assignee}
          onChange={(assignee) => set({ assignee })}
          placeholder="Toată lumea"
          options={assigneeOptions}
          className="w-full sm:w-48"
        />
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreatingOn('')}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-fixed-white hover:bg-brand-700"
          >
            <Plus size={15} /> Postare nouă
          </button>
        )}
      </div>

      {!lockedClientId && legend.length > 0 && (
        <div className="flex flex-wrap gap-3 text-xs text-gray-600">
          {legend.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: clientColor(c) }}
              />
              {clientLabel(c)}
            </span>
          ))}
        </div>
      )}

      {data?.capped && (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          Sunt prea multe postări în luna asta; restrânge cu filtrele.
        </p>
      )}

      {isLoading ? (
        <SkeletonTable rows={6} />
      ) : isError ? (
        <p className="py-8 text-center text-sm text-red-600">Postările nu au putut fi încărcate.</p>
      ) : isMobile ? (
        <MobileAgenda
          weeks={weeks}
          byDay={byDay}
          onOpen={setPreview}
          showClient={!lockedClientId}
        />
      ) : (
        <CalendarDnd<SocialPost>
          onMove={(post, dayKey) =>
            movePost.mutate(
              { id: post.id, publishAt: publishAtOnDay(post.publishAt, dayKey) },
              { onError: () => toast.show('Postarea nu a putut fi mutată.') },
            )
          }
          renderOverlay={(post) => (
            <PostCard post={post} showClient={!lockedClientId} onOpen={() => {}} />
          )}
        >
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-card">
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
              {weekdayLabels(locale).map((name) => (
                <div
                  key={name}
                  className="truncate px-1 py-1.5 text-center text-xs font-medium capitalize text-gray-500"
                >
                  {name}
                </div>
              ))}
            </div>
            {weeks.map((week) => (
              <div
                key={week[0].key}
                className="grid grid-cols-7 border-b border-gray-100 last:border-b-0"
              >
                {week.map((day) => (
                  <DayCell
                    key={day.key}
                    dayKey={day.key}
                    dayNumber={day.date.getDate()}
                    inMonth={day.inMonth}
                    isToday={day.key === todayKey}
                    posts={byDay.get(day.key) ?? []}
                    showClient={!lockedClientId}
                    canDrag={canEdit}
                    onOpen={setPreview}
                    onCreate={canCreate ? () => setCreatingOn(day.key) : undefined}
                  />
                ))}
              </div>
            ))}
          </div>
        </CalendarDnd>
      )}

      {preview && (
        <PostPreview post={preview} onClose={() => setPreview(null)} showClient={!lockedClientId} />
      )}

      {creatingOn !== null && (
        <PostFormModal
          clientId={lockedClientId ?? (filters.clients.length === 1 ? filters.clients[0] : '')}
          lockClient={Boolean(lockedClientId)}
          date={creatingOn || undefined}
          onClose={() => setCreatingOn(null)}
        />
      )}
    </div>
  );
}

function PostCard({
  post,
  showClient,
  onOpen,
}: {
  post: SocialPost;
  showClient: boolean;
  onOpen: (post: SocialPost) => void;
}) {
  const color = post.client ? clientColor(post.client) : '#9ca3af';
  return (
    <button
      type="button"
      onClick={() => onOpen(post)}
      title={post.caption ?? post.title}
      className="block w-full rounded border border-gray-200 bg-card px-1.5 py-1 text-left text-[11.5px] leading-tight hover:border-gray-400"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <span className="flex items-center gap-1">
        <span
          className="inline-block h-2 w-2 shrink-0 rounded-sm"
          style={{ backgroundColor: PLATFORM_COLORS[post.platform] }}
          title={PLATFORM_LABELS[post.platform]}
        />
        <span className="shrink-0 tabular-nums text-gray-500">{timeOf(post.publishAt)}</span>
        {showClient && post.client && (
          <span className="truncate font-medium" style={{ color }}>
            {clientLabel(post.client)}
          </span>
        )}
        {post.mediaUrl && <Video size={11} className="ml-auto shrink-0 text-gray-400" />}
      </span>
      <span className="mt-0.5 block truncate text-gray-800">{post.title}</span>
      <span
        className={`mt-0.5 inline-block rounded px-1 text-[10px] ${APPROVAL_TONES[post.approvalStatus]}`}
      >
        {APPROVAL_LABELS[post.approvalStatus]}
      </span>
    </button>
  );
}

function DayCell({
  dayKey,
  dayNumber,
  inMonth,
  isToday,
  posts,
  showClient,
  canDrag,
  onOpen,
  onCreate,
}: {
  dayKey: string;
  dayNumber: number;
  inMonth: boolean;
  isToday: boolean;
  posts: SocialPost[];
  showClient: boolean;
  canDrag: boolean;
  onOpen: (post: SocialPost) => void;
  onCreate?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? posts : posts.slice(0, MAX_VISIBLE);
  return (
    <DroppableCalendarDay
      dayKey={dayKey}
      className={`group min-h-[118px] border-r border-gray-100 p-1 last:border-r-0 ${
        inMonth ? '' : 'bg-gray-50/60'
      }`}
    >
      <div className="mb-1 flex items-center justify-between">
        <span
          className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs ${
            isToday
              ? 'bg-brand-600 font-semibold text-fixed-white'
              : inMonth
                ? 'text-gray-700'
                : 'text-gray-400'
          }`}
        >
          {dayNumber}
        </span>
        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            aria-label="Postare nouă în ziua asta"
            className="rounded p-0.5 text-gray-400 opacity-0 hover:bg-gray-100 hover:text-gray-700 group-hover:opacity-100 touch:opacity-100"
          >
            <Plus size={13} />
          </button>
        )}
      </div>
      <div className="space-y-1">
        {visible.map((p) => (
          <DraggableCalendarItem key={p.id} id={p.id} item={p} dayKey={dayKey} disabled={!canDrag}>
            <PostCard post={p} showClient={showClient} onOpen={onOpen} />
          </DraggableCalendarItem>
        ))}
        {posts.length > MAX_VISIBLE && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full text-left text-[11px] text-gray-500 hover:text-gray-800"
          >
            {expanded ? 'Mai puțin' : `+${posts.length - MAX_VISIBLE} postări`}
          </button>
        )}
      </div>
    </DroppableCalendarDay>
  );
}

/** Phones: a seven-column grid of cards does not fit, so the month is a list. */
function MobileAgenda({
  weeks,
  byDay,
  onOpen,
  showClient,
}: {
  weeks: ReturnType<typeof monthGrid>;
  byDay: Map<string, SocialPost[]>;
  onOpen: (post: SocialPost) => void;
  showClient: boolean;
}) {
  const days = weeks.flat().filter((d) => d.inMonth && byDay.has(d.key));
  if (days.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-400">Nicio postare luna asta.</p>;
  }
  return (
    <div className="space-y-3">
      {days.map((d) => (
        <div key={d.key}>
          <p className="mb-1 text-xs font-medium capitalize text-gray-500">
            {d.date.toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
          <div className="space-y-1.5">
            {(byDay.get(d.key) ?? []).map((p) => (
              <PostCard key={p.id} post={p} showClient={showClient} onOpen={onOpen} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PostPreview({
  post,
  onClose,
  showClient,
}: {
  post: SocialPost;
  onClose: () => void;
  showClient: boolean;
}) {
  const navigate = useNavigate();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-card shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span
                className="rounded-full px-2 py-0.5 font-medium text-fixed-white"
                style={{ backgroundColor: PLATFORM_COLORS[post.platform] }}
              >
                {PLATFORM_LABELS[post.platform]}
              </span>
              <span className="text-gray-500">{FORMAT_LABELS[post.format]}</span>
              <span className={`rounded-full px-2 py-0.5 ${APPROVAL_TONES[post.approvalStatus]}`}>
                {APPROVAL_LABELS[post.approvalStatus]}
              </span>
            </div>
            <h2 className="mt-1.5 text-base font-semibold text-gray-900">{post.title}</h2>
            <p className="text-sm text-gray-500">
              {showClient && post.client && (
                <span style={{ color: clientColor(post.client) }}>
                  {clientLabel(post.client)} ·{' '}
                </span>
              )}
              {new Date(post.publishAt).toLocaleString('ro-RO', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 px-5 py-4 text-sm">
          {post.caption ? (
            <p className="whitespace-pre-wrap text-gray-800">{post.caption}</p>
          ) : (
            <p className="text-gray-400">Fără text încă.</p>
          )}
          {post.mediaUrl && (
            <a
              href={post.mediaUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-gray-700 hover:bg-gray-50"
            >
              <Video size={14} /> Deschide video <ExternalLink size={12} />
            </a>
          )}
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-500">
            <dt>Responsabil</dt>
            <dd className="text-gray-800">{post.assignee?.name ?? '—'}</dd>
            <dt>Status producție</dt>
            <dd className="text-gray-800">{post.status.name}</dd>
            {post.project && (
              <>
                <dt>Proiect</dt>
                <dd className="text-gray-800">{post.project.name}</dd>
              </>
            )}
          </dl>
        </div>
        <div className="flex justify-end border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={() => navigate(`/tasks/${encodeURIComponent(post.identifier ?? post.id)}`)}
            className="ui-btn-primary"
          >
            Deschide postarea
          </button>
        </div>
      </div>
    </div>
  );
}
